import React, { useState } from 'react';

import { Box, COLORS, Text, TextButton } from '@clutter/clean';

import { useSharedCheckoutContext } from '@root/components/checkout/context';
import { StorageCheckoutStepProps } from '@root/components/checkout/types';
import { useTrack } from '@root/initializers/wt';
import { Commitment as CommitmentType } from '@root/resources/types/commitment';

import { InfoTooltip } from '@shared/info_tooltip';

import { WHOLE_NUMBER_OPTIONS, formatCurrency } from '@utils/formatters';
import { useTieredStoragePricing } from '@utils/hooks/ab_testing';
import { useStoragePriceCompletedFunnelEvent } from '@utils/hooks/funnel_events/use_storage_price_completed_funnel_event';
import { useBreakpoints } from '@utils/hooks/use_breakpoints';
import { useLastDefined } from '@utils/hooks/use_last_defined';
import { extractStoragePrice } from '@utils/pricing/pricing_set';

import { FeaturesOverlay } from '../../subcomponents/features_overlay';
import { SMART_STORAGE_COMMITMENT_FEATURES } from '../../subcomponents/features_overlay/data';
import { Superscript } from './bundles/superscript';
import { Banner } from './commitment/banner';
import { Cards } from './commitment/cards';
import { TieredCards } from './commitment/tiered_cards';

type CommitmentStepProps = StorageCheckoutStepProps & {
  movingCheckoutFlow: boolean;
};

export const Commitment: React.FC<CommitmentStepProps> = ({
  onChange,
  scrollToStep,
  values: { commitment, planSize },
  movingCheckoutFlow,
}) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const { isMobile } = useBreakpoints();
  const {
    pricingSummary: summary,
    defaultPricingSummary,
    pricingSet,
  } = useSharedCheckoutContext();
  const track = useTrack({ action: 'click' });

  const pricingSummary = useLastDefined(summary);
  const pricingSummaryLoading = pricingSummary
    ? !summary
    : !defaultPricingSummary || !!planSize;
  const tieredPricing = useTieredStoragePricing();
  const trackStoragePriceCompleted = useStoragePriceCompletedFunnelEvent();
  const visible = !movingCheckoutFlow || (movingCheckoutFlow && planSize);

  const handleChange = (term: CommitmentType) => {
    if (planSize && pricingSet) {
      // This can't be read from the pricing summary as fees are currently
      // loaded asynchronously.
      const amount = extractStoragePrice(term, planSize, pricingSet);
      trackStoragePriceCompleted({
        planSize,
        commitment: term,
        amount: amount!,
      });
    }

    onChange('commitment', term);
    !isMobile && scrollToStep();
  };

  const handleLearnMoreClick = () => {
    track({
      objectName: 'features',
      objectType: 'button',
      label: 'Learn more about moving',
    });
    setShowOverlay(true);
  };

  if (!visible) {
    return null;
  }
  return (
    <>
      <Box margin="48px 0 0">
        <Box.Flex justifyContent="space-between">
          <Text.Title size="small" color={COLORS.tealDark}>
            {tieredPricing
              ? 'Choose your plan and storage term'
              : 'How long would you like to commit?'}
          </Text.Title>
          <Box.FlexItem
            flexShrink={0}
            display={['none', null, null, 'initial']}
            alignSelf="center"
          >
            <TextButton onClick={handleLearnMoreClick}>Learn more</TextButton>
          </Box.FlexItem>
        </Box.Flex>
        <Box margin="4px 0 0">
          <Text.Body color={COLORS.storm}>
            {tieredPricing ? (
              <>
                Store longer, save more! Enjoy free<Superscript>1</Superscript>{' '}
                initial pickup, final delivery, and extra appointments with our
                4 or 8-month minimum plans.
              </>
            ) : (
              <>
                Your first pickup to move items into storage and final delivery
                to move items out of storage is included for free
                <Superscript>1</Superscript> with every storage plan. We’ll
                provide additional appointments free of charge with a 4 or 8
                month minimum storage commitment.
              </>
            )}
          </Text.Body>
        </Box>
        {(!!defaultPricingSummary?.freeDuration ||
          !!pricingSummary?.freeDuration) &&
          tieredPricing && (
            <Box margin="8px 0 0">
              <Text.Caption color={COLORS.hippo}>
                <Superscript>1</Superscript> Free up to{' '}
                {defaultPricingSummary?.freeDuration ||
                  pricingSummary?.freeDuration}{' '}
                hours and then charges apply.{' '}
                {pricingSummary?.fullServiceLaborRateAmount && (
                  <InfoTooltip
                    contents={`Billed hourly at ${formatCurrency(
                      pricingSummary?.fullServiceLaborRateAmount,
                      WHOLE_NUMBER_OPTIONS,
                    )}/hour thereafter.`}
                  />
                )}
              </Text.Caption>
            </Box>
          )}
        <Box>
          <Box margin="16px 0 0" position="relative">
            {!!tieredPricing ? (
              <TieredCards
                commitment={commitment}
                pricingSummary={pricingSummary}
                pricingSummaryLoading={pricingSummaryLoading}
                onChange={(selectedCommitment: CommitmentType) =>
                  handleChange(selectedCommitment)
                }
                scrollToStep={scrollToStep}
              />
            ) : (
              <Cards
                commitment={commitment}
                pricingSummary={pricingSummary}
                defaultPricingSummary={defaultPricingSummary}
                onChange={(selectedCommitment: CommitmentType) =>
                  handleChange(selectedCommitment)
                }
                scrollToStep={scrollToStep}
              />
            )}
          </Box>
          {(!!defaultPricingSummary?.freeDuration ||
            !!pricingSummary?.freeDuration) &&
            !tieredPricing && (
              <Box margin="16px 0 0">
                <Text.Caption color={COLORS.hippo}>
                  <Superscript>1</Superscript> Free up to{' '}
                  {defaultPricingSummary?.freeDuration ||
                    pricingSummary?.freeDuration}{' '}
                  hours and then charges apply.{' '}
                  {pricingSummary?.fullServiceLaborRateAmount && (
                    <InfoTooltip
                      contents={`Billed hourly at ${formatCurrency(
                        pricingSummary?.fullServiceLaborRateAmount,
                        WHOLE_NUMBER_OPTIONS,
                      )}/hour thereafter.`}
                    />
                  )}
                </Text.Caption>
              </Box>
            )}
        </Box>
        {tieredPricing && (
          <Banner
            planSize={planSize}
            commitment={commitment}
            pricingSummary={pricingSummary}
          />
        )}
      </Box>
      {showOverlay && (
        <FeaturesOverlay
          isOpen={showOverlay}
          onClose={() => setShowOverlay(false)}
          content={SMART_STORAGE_COMMITMENT_FEATURES}
        />
      )}
    </>
  );
};
